// claude-version-probe.ts
//
// N9 (phase 05): the ONE `claude --version` spawn shared by doctor/reverify/
// run/supervisor — three call sites (autopilot-ops-checks-cli-wiring.ts,
// supervisor.ts, supervisor-mission-run-startup.ts) each had their own
// identical `defaultProbeClaudeVersion` copy before this module existed.
// Neutral module (no `*-cli-wiring` import) so the runtime supervisor layer
// never imports from the CLI-wiring layer (layering rule).
import { spawnAsyncCapture } from "../spawn-async-capture-with-timeout.js";

/**
 * Shared `<binary> --version` probe — fallback `(unavailable)` on any spawn
 * failure. D-03 (re-review backlog 2026-09-26): `autopilot doctor` probes
 * `git`/`python3` through this same seam, so every binary check degrades the
 * same way instead of growing its own spawn copy.
 */
export async function probeBinaryVersion(binary: string): Promise<string> {
  try {
    const res = await spawnAsyncCapture(binary, ["--version"], { timeoutMs: 10_000 });
    return res.status === 0 ? res.stdout.trim() : "(unavailable)";
  } catch {
    // spawnAsyncCapture REJECTS on the child's `error` event (ENOENT when the
    // binary is not on PATH, EACCES when not executable) — measured
    // 2026-09-26: `autopilot doctor --json` with python3 off PATH crashed with
    // "spawn python3 ENOENT" instead of reporting the `fail` check (and the
    // claude probe had the same latent crash). A binary that cannot be
    // spawned at all is exactly "(unavailable)".
    return "(unavailable)";
  }
}

/** Shared `claude --version` probe — fallback `(unavailable)` on any spawn failure. */
export async function probeClaudeVersion(): Promise<string> {
  return probeBinaryVersion("claude");
}
